import MapboxDraw from '@mapbox/mapbox-gl-draw';
import DrawRectangle from 'mapbox-gl-draw-rectangle-mode';
import { useControl, useMap } from 'react-map-gl';
import { SnapModeDrawStyles } from 'mapbox-gl-draw-snap-mode';
import CutLineMode from 'mapbox-gl-draw-cut-line-mode';
import MapboxSnap from 'mapbox-gl-snap/dist/esm/MapboxSnap';
import type { MapRef, ControlPosition } from 'react-map-gl';
import { useEffect, useRef } from 'react';

function cleanupSnapArtifacts(map: mapboxgl.Map) {
    const ids = [
        'mapbox-gl-snap-radius',
        'mapbox-gl-snap-radius-line',
        'mapbox-gl-snap-vertex',
        'mapbox-gl-snap-midpoint',
        'mapbox-gl-snap-edge',
        'snap-radius-layer',
        'snap-radius-source',
        'snap-helper-circle',
    ];

    ids.forEach(id => {
        if (map.getLayer(id)) {
            map.removeLayer(id);
        }
        if (map.getSource(id)) {
            map.removeSource(id);
        }
    });
}

type DrawControlProps = ConstructorParameters<typeof MapboxDraw>[0] & {
    position?: ControlPosition;

    onCreate?: (evt: MapboxDraw.DrawCreateEvent) => void;
    onUpdate?: (evt: MapboxDraw.DrawUpdateEvent) => void;
    onDelete?: (evt: MapboxDraw.DrawDeleteEvent) => void;
    onDrawModeChange?: (evt: MapboxDraw.DrawModeChangeEvent) => void;
    allLayersIds?: string[];
};

export let drawRef: MapboxDraw | null = null;

export default function DrawControl({ allLayersIds, ...props }: DrawControlProps) {
    const { mapboxMapId } = useMap();
    const snapRef = useRef<MapboxSnap | null>(null);

    const modes = {
        modes: {
            ...MapboxDraw.modes,
            draw_rectangle: DrawRectangle,
            cut_line: CutLineMode,
        },
        styles: SnapModeDrawStyles,
        userProperties: true,
        guides: false,
    };

    useEffect(() => {
        if (!mapboxMapId || !drawRef || !allLayersIds?.length) {
            return;
        }

        const initializeSnap = () => {
            if (snapRef.current) {
                return;
            }

            const map = mapboxMapId.getMap();

            cleanupSnapArtifacts(map);

            try {
                snapRef.current = new MapboxSnap({
                    map,
                    drawing: drawRef!,
                    options: {
                        layers: allLayersIds,
                        radius: 5,
                        rules: ['vertex', 'midpoint', 'edge'],
                    },
                });
            } catch (e) {
                console.error('Replan. MapboxSnap error:', e);
            }
        };

        const map = mapboxMapId.getMap();

        if (map.isStyleLoaded()) {
            initializeSnap();
        } else {
            map.once('load', initializeSnap);
        }

        return () => {
            map.off('load', initializeSnap);
            if (snapRef.current) {
                cleanupSnapArtifacts(map);
                snapRef.current = null;
            }
        };
    }, [mapboxMapId, allLayersIds]);

    //TODO this is dangerous to have global variable => find a proper solution how to use drawer instance in other components if possible.
    drawRef = useControl<MapboxDraw>(
        ({ map }: { map: MapRef }) => {
            const drawRef = new MapboxDraw({
                ...props,
                ...modes,
            });

            return drawRef;
        },
        ({ map }: { map: MapRef }) => {
            props.onCreate && map.on('draw.create', props.onCreate);
            props.onUpdate && map.on('draw.update', props.onUpdate);
            props.onDelete && map.on('draw.delete', props.onDelete);
            props.onDrawModeChange && map.on('draw.modechange', props.onDrawModeChange);
        },
        ({ map }: { map: MapRef }) => {
            props.onCreate && map.off('draw.create', props.onCreate);
            props.onUpdate && map.off('draw.update', props.onUpdate);
            props.onDelete && map.off('draw.delete', props.onDelete);
            props.onDrawModeChange && map.off('draw.modechange', props.onDrawModeChange);
        },
        {
            position: props.position,
        }
    );

    return null;
}
